import * as React from "react";
import { Spin } from "antd";
import { useAuthStore } from "../stores/useAuthStore";
import { useCurrentUser } from "../hooks/useAuth";
import { useUsageTracker } from "../hooks/useUsageTracker";
import type { AuthUser } from "../types/auth";
import Login from "./Login";
import TaskpaneApp from "./TaskpaneApp";

interface AppProps {
  title: string;
}

const App: React.FC<AppProps> = ({ title }) => {
  const [hydrated, setHydrated] = React.useState(false);
  const hydrate = useAuthStore((s) => s.hydrate);
  const accessToken = useAuthStore((s) => s.accessToken);

  React.useEffect(() => {
    hydrate();
    setHydrated(true);
  }, [hydrate]);

  if (!hydrated) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!accessToken) {
    return <Login />;
  }

  return <AuthedShell title={title} />;
};

type AuthedShellProps = {
  title: string;
};

/** Renders only after `/auth/me` succeeds — starts session time tracking here. */
const AuthedShellContent: React.FC<{ title: string; user: AuthUser }> = ({ title, user }) => {
  useUsageTracker();
  return (
    <div style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <TaskpaneApp title={title} user={user} />
    </div>
  );
};

const AuthedShell: React.FC<AuthedShellProps> = ({ title }) => {
  const { data: user, isLoading, isFetching } = useCurrentUser();

  if (isLoading || (isFetching && !user)) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return <AuthedShellContent title={title} user={user} />;
};

export default App;
